import { useState, KeyboardEvent, MouseEvent } from 'react';
import { css } from '@emotion/react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';

import renameRun from 'store/renameRun';
import IRunEntry from 'types/IRunEntry';

const wrapperCss = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  & .edit-icon {
    opacity: 0;
    transition: opacity 0.1s ease-in;
  }

  &:hover .edit-icon {
    opacity: 1;
  }
`;

interface Props {
  run: IRunEntry;
}

export default function EditableNameCell({ run }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(run.name);
  const [submitting, setSubmitting] = useState(false);

  function startEdit(event: MouseEvent) {
    event.stopPropagation();
    setDraft(run.name);
    setEditing(true);
  }

  function cancel() {
    setDraft(run.name);
    setEditing(false);
  }

  async function commit() {
    if (submitting) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === run.name) {
      cancel();
      return;
    }
    setSubmitting(true);
    try {
      await renameRun(run.id, trimmed);
      setEditing(false);
    } catch {
      // Optimistic update was reverted in renameRun; keep editing for retry.
    } finally {
      setSubmitting(false);
    }
  }

  if (!editing) {
    return (
      <span css={wrapperCss}>
        {run.name}
        <IconButton className="edit-icon" size="small" aria-label="Rename" onClick={startEdit}>
          <EditIcon fontSize="inherit" />
        </IconButton>
      </span>
    );
  }

  return (
    <TextField
      size="small"
      variant="standard"
      autoFocus
      fullWidth
      value={draft}
      disabled={submitting}
      slotProps={{ htmlInput: { maxLength: 200 } }}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          cancel();
        }
      }}
    />
  );
}
